<template>
  <div class="auth-registration">
    <!-- The browser otherwise refills these from a previous registration on this
         device, so a second person onboarding here starts on someone else's answers. -->
    <form class="auth-content" autocomplete="off" @submit="handleSubmit">
      <div class="auth-context left">
        <span class="auth-title">{{ localize('auth.page.registration.title') }}</span>
        <span class="auth-subtitle">{{ localize('auth.page.registration.subtitle') }}</span>
      </div>

      <div class="auth-content-wrapper">
        <div v-if="gamForm" class="auth-form registration">
          <div class="form-row">
            <gam-input v-bind="firstName" v-model="gamForm.form.firstName" />
            <gam-input v-bind="lastName" v-model="gamForm.form.lastName" />
          </div>

          <div class="form-row username-row">
            <div class="input-with-support">
              <gam-input v-bind="username" v-model="gamForm.form.username" @update="checkUsername" />
              <gam-support-text v-if="!usernameField.error" label="Letters, dots, underscores & numbers." />
            </div>

            <gam-input v-bind="emoji" v-model="gamForm.form.emoji" class="emoji" />
          </div>

          <div class="form-row">
            <gam-input-date v-model="gamForm.form.birthDate" />
          </div>
        </div>
      </div>

      <input type="submit" class="hidden" />
    </form>

    <transition name="dropdown" mode="out-in"> </transition>

    <div class="auth-methods-group">
      <GamButtonMessage class="error-message" v-if="errorMessage" v-bind="errorMessage" />
      <div class="methods-row">
        <gam-button v-bind="getContinueButton" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterAuthType } from '@/core/data/config/uiConfig.interface';
import type { BaseUserInfo } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import FormHelper from '@/core/helpers/form.helper';
import { useAppStore } from '@/stores/app.store';
import { useAuthStore } from '@/stores/auth.store';
import { useFormStore } from '@/stores/form.store';
import GamButton from '@/views/components/GamButton.vue';
import GamButtonMessage from '@/views/components/GamButtonMessage.vue';
import GamInput from '@/views/components/GamInput.vue';
import GamInputDate from '@/views/components/GamInputDate.vue';
import GamSupportText from '@/views/components/GamSupportText.vue';
import {
  GamButtonMessageVariant,
  GamButtonSize,
  GamButtonVariant,
} from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { FormId } from '@/views/composables/constants/form.constants';
import type { GamButtonMessageType, GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamInputType } from '@/views/composables/models/components/GamInput';
import type { BaseUserForm, BaseUserValidator } from '@/views/composables/models/form.interface';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { subYears } from 'date-fns';
import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { generateUsername, getNamesFromEmail, isApplePrivateRelayEmail } from '../../../../../../shared/utils';

const formId = FormId.AUTH_REGISTRATION;
const isAuthLoading = ref<boolean>(true);
const errorMessage = ref<GamButtonMessageType | null>(null);
const isLoading = ref<boolean>(false);

const emojiValidator = FormHelper.emojiValidator;
const formStore = useFormStore<BaseUserForm, BaseUserValidator>(formId)();
const { gamForm, usernameField } = storeToRefs(formStore);
const router = useRouter();
const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);
// A handle the account already holds stays valid until it is changed.
const usernameValidator = (username: string): boolean =>
  username === userDetails.value?.username || FormHelper.usernameValidator(username);
const appStore = useAppStore();
const { isAutomation } = storeToRefs(appStore);

const authUser = ref<BaseUserInfo>();

const getForm = reactive({
  firstName: '',
  lastName: '',
  username: '',
  emoji: '♟️',
  birthDate: isAutomation.value ? subYears(new Date(), 21) : undefined,
});

const dateValidator = (date: Date): boolean => {
  return FormHelper.isValidDate(date, gambits.configService.getAuthMinAge());
};

const getRules = {
  firstName: { required },
  lastName: { required },
  username: { required, usernameValidator },
  emoji: { emojiValidator },
  birthDate: { required, dateValidator },
};

// Seeded here rather than in onBeforeMount below: that hook is async, so the
// template paints during its first await. Left until after, the first render
// binds the inputs to whatever the store still holds - the previous person's
// answers, since the store is only disposed on a completed registration.
formStore.setForm({ form: getForm, rules: getRules });

// A Lichess signup arrives with its IRL rating already resolved by the API, so the ratings
// step has nothing left to ask for and is skipped.
const getNextStepAfterDetails = (): RouterAuthType =>
  userDetails.value?.rating ? RouterAuthType.AUTH_LOCATION : RouterAuthType.AUTH_RATINGS;

onBeforeMount(async () => {
  await authStore.fetchUserInfo(true);

  const { firstName, lastName, birthDate, username, emoji } = userDetails.value || {};

  // Require login
  if (!authStore.isAuthenticated()) {
    return router.push('/auth');
  }

  // Advance past the personal details step
  if (firstName && lastName && birthDate && username && emoji) {
    return router.push({ name: getNextStepAfterDetails() });
  }

  await generateDefaults();
  await getAuthUser();

  checkUsername();

  isAuthLoading.value = false;
});

const v$ = useVuelidate(getRules, getForm);

function capitalize(word: string): string {
  if (word.length === 0) return '';

  return word[0].toUpperCase() + word.substring(1);
}

const generateDefaults = async () => {
  if (!gamForm.value?.form) return;

  const names = getNamesFromEmail(user.value?.email || '');
  gamForm.value.form.firstName = capitalize(names.firstName || '');
  gamForm.value.form.lastName = capitalize(names.lastName || '');
  gamForm.value.form.username = isApplePrivateRelayEmail(user.value?.email || '') ? '' : generateUsername(names);
  gamForm.value.form.birthDate = isAutomation.value ? subYears(new Date(), 21) : undefined;
};

const isInvalid = computed((): boolean => {
  if (!gamForm.value) return true;

  if (!usernameField.value.isValidated) return true;

  if (!gamForm.value.form.firstName.length) return true;
  if (!gamForm.value.form.lastName.length) return true;

  if (!gamForm.value.form.birthDate) return true;

  return v$.value.$invalid;
});

const checkUsername = debounce(() => {
  nextTick(() => {
    if (userDetails.value?.username && userDetails.value?.username === gamForm.value?.form.username) {
      formStore.setUsernameValidated(true);
      formStore.setUsernameError(null);
    } else {
      FormHelper.checkUsername(formId, gamForm.value?.form.username || '', v$);
    }
  });
}, 400);

const getAuthUser = async (): Promise<void> => {
  const result = await gambits.userService.getUserInfo(true);

  if (result.data) {
    authUser.value = result.data;
    if (gamForm.value?.form) {
      gamForm.value.form.firstName = authUser.value?.firstName || gamForm.value.form.firstName || '';
      gamForm.value.form.lastName = authUser.value?.lastName || gamForm.value.form.lastName || '';
      if (!isApplePrivateRelayEmail(authUser.value?.email || '')) {
        gamForm.value.form.username = authUser.value?.username || gamForm.value.form.username || '';
      }
      gamForm.value.form.birthDate = isAutomation.value
        ? subYears(new Date(), 21)
        : authUser.value?.birthDate
          ? new Date(authUser.value?.birthDate)
          : undefined;
    }
  }
};

const firstName = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_NAME,
    value: gamForm.value?.form.firstName,
    placeholder: 'auth.page.registration.form.firstName.placeholder',
  };
});

const lastName = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_SURNAME,
    value: gamForm.value?.form.lastName,
    placeholder: 'auth.page.registration.form.lastName.placeholder',
  };
});

const username = computed((): GamInputType => {
  return {
    name: GamInputName.GAM_USERNAME,
    value: gamForm.value?.form.username || '',
    placeholder: 'auth.page.registration.form.username.placeholder',
    isLoading: usernameField.value.isLoading,
    isError: usernameField.value.isValidated === false,
    message: usernameField.value.error,
    rightIcon: usernameField.value.isValidated ? GamIconName.CHECK : undefined,
    maxLength: 30,
    isPrimary: true,
    isLowerCase: true,
    isUsername: true,
  };
});

const emoji = computed((): GamInputType => {
  return {
    name: GamInputName.EMOJI,
    value: gamForm.value?.form.emoji,
    placeholder: 'auth.page.registration.form.emoji.placeholder',
    isEmoji: true,
    maxLength: 1,
  };
});

async function handleSubmit(event: Event) {
  event?.preventDefault();
  if (isInvalid.value) return;

  if (!gamForm.value?.form || !user.value?.email) return;

  errorMessage.value = null;
  isLoading.value = true;
  const result = await gambits.authService.submitPersonalInfo({ ...gamForm.value.form });
  isLoading.value = false;

  if (result.error) {
    errorMessage.value = {
      label: result.error.message,
      variant: GamButtonMessageVariant.ERROR,
    };

    return;
  }

  await router.push({ name: getNextStepAfterDetails() });

  formStore.$dispose();
}

const getContinueButton = computed((): GamButtonType => {
  return {
    variant: GamButtonVariant.PRIMARY,
    size: GamButtonSize.LARGE,
    label: 'auth.page.registration.continue.button.label',
    isFullSize: true,
    isCentered: true,
    isDisabled: isInvalid.value,
    isLoading: isLoading.value,
    onClick: handleSubmit,
  };
});

onMounted(() => {
  document.querySelector('.auth-modal')?.classList.add('registration');
});

onUnmounted(() => {
  document.querySelector('.auth-modal')?.classList.remove('registration');
});
</script>

<style lang="scss">
// .auth-modal.registration {
//   max-width: 353px !important;
// }
</style>

<style scoped lang="scss">
.auth-registration {
  .auth-loading {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-dark-30);
    z-index: 600;
  }

  .auth-content {
    gap: var(--spacing-menu);

    .auth-content-wrapper {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      gap: var(--spacing-menu) !important;

      .auth-form {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-xl);
        align-self: stretch;
        padding-bottom: var(--spacing-l);

        .form-row {
          width: 100%;
          display: flex;
          gap: var(--spacing-md);

          .emoji {
            width: 80px;
          }
        }

        &.images {
          flex-direction: row;

          @media (min-width: 976px) {
            margin-bottom: var(--spacing-menu);
          }

          .image {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-md);

            .label {
              font-size: 14px;
              font-weight: 400;
              color: var(--color-white-70);
              line-height: 110%;
            }
          }
        }
      }
    }
  }

  .input-with-support {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    position: relative;
    width: 100%;

    .gam-support-text {
      position: absolute;
      top: 3.75rem;
      padding-left: 23px;
      width: max-content;
    }
  }

  .input-with-support {
    padding-bottom: 1rem;
  }
}

.error-message {
  margin-top: calc(var(--spacing-xl) * -1);
  margin-bottom: 1rem;
}
</style>
